'use client'

import { ChevronsUpDown, Gift, LogOut, Moon, Plus, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar'
import { logout } from '../../modules/auth/logout'
import { NavigationLink } from '../navigation/types'
import { ClientSelector } from '../navigation/ClientSelector'
import Link from 'next/link'
import { Separator } from '@/components/ui/separator'
import { Small, XSmall } from '@/components/ui/typography'
import { Icon } from '../Icon'
import { LocalStorageKey } from '@/lib/storageConstants'
import {
    SIDEBAR_ICON_SLOT_CLASS,
    SIDEBAR_RAIL_HIDE_CLASS,
} from '@/components/ui/sidebarExpandState'
import { cn } from '@/lib/utils'

export function NavUser({
    user,
    client,
    menuItems = [],
    onClientChange,
    onboardNewClientLink,
}: {
    user: {
        name: string
        email: string
        avatar: string
    }
    client?: any
    menuItems?: NavigationLink[]
    onClientChange?: (clientHash: string) => void
    /** Shown in the Account section (with client selector) */
    onboardNewClientLink?: NavigationLink | null
}) {
    const { isMobile, setHoverLocked } = useSidebar()
    const { resolvedTheme, setTheme } = useTheme()
    const isDark = resolvedTheme === 'dark'

    const handleLogout = async () => {
        await logout()
        window.localStorage.removeItem(LocalStorageKey.CLIENT_HASH)
        window.location.href = '/login'
    }

    const handleItemClick = (item: NavigationLink, e: React.MouseEvent) => {
        if (item.openInNewTab && item.href) {
            e.preventDefault()
            window.open(item.href, '_blank')
            return
        }
    }

    const standardItems = menuItems.filter((item) => !item.internalOnly)
    const internalItems = menuItems.filter((item) => item.internalOnly)

    const renderMenuLink = (item: NavigationLink) => (
        <DropdownMenuItem
            key={item.value}
            asChild
            className="truncate px-2 py-1.5 text-sm"
        >
            <Link
                href={item.href}
                onClick={(e) => handleItemClick(item, e)}
                className="flex w-full items-center gap-2 truncate"
            >
                {item.value === 'referrals' ? (
                    <Gift className="size-4" />
                ) : item.icon ? (
                    <Icon
                        type={item.icon}
                        color="currentColor"
                        width={16}
                        height={16}
                    />
                ) : null}
                <span>{item.label}</span>
            </Link>
        </DropdownMenuItem>
    )

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu onOpenChange={setHoverLocked}>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="h-12 gap-0 p-0 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                        >
                            <span className={SIDEBAR_ICON_SLOT_CLASS}>
                                <Avatar className="size-8 rounded-lg">
                                    <AvatarImage
                                        src={user.avatar}
                                        alt={user.name}
                                    />
                                    <AvatarFallback className="rounded-lg">
                                        {user.name.charAt(0) || 'U'}
                                    </AvatarFallback>
                                </Avatar>
                            </span>
                            <div
                                className={cn(
                                    'grid min-w-0 flex-1 pr-3 text-left text-sm leading-tight',
                                    SIDEBAR_RAIL_HIDE_CLASS,
                                )}
                            >
                                <span className="truncate font-semibold">
                                    {user.name}
                                </span>
                                <span className="truncate text-xs">
                                    {user.email}
                                </span>
                            </div>
                            <ChevronsUpDown
                                className={cn(
                                    'mr-3 size-4 shrink-0',
                                    SIDEBAR_RAIL_HIDE_CLASS,
                                )}
                            />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="max-h-[calc(100vh-20px)] w-64 overflow-auto rounded-lg"
                        side={isMobile ? 'bottom' : 'right'}
                        align="end"
                        sideOffset={8}
                    >
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 p-2 text-left text-sm">
                                <Avatar className="size-10 rounded-lg">
                                    <AvatarImage
                                        src={user.avatar}
                                        alt={user.name}
                                    />
                                    <AvatarFallback className="rounded-lg">
                                        {user.name.charAt(0) || 'U'}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate text-base font-semibold">
                                        {user.name}
                                    </span>
                                    <XSmall className="truncate text-muted-foreground">
                                        {client?.name || 'No client selected'}
                                    </XSmall>
                                    <XSmall className="truncate text-muted-foreground">
                                        {user.email}
                                    </XSmall>
                                </div>
                            </div>
                        </DropdownMenuLabel>
                        <Separator className="my-1" />

                        {/* Account section: skip when neither client selector nor onboard link is provided (e.g. styleguide) */}
                        {(onClientChange || onboardNewClientLink?.enabled) && (
                            <div className="px-2 py-1.5">
                                <Small className="mb-1.5 block text-xs font-medium uppercase text-muted-foreground">
                                    Account
                                </Small>
                                {onClientChange && (
                                    <ClientSelector
                                        client={client}
                                        onClientChange={onClientChange}
                                        className="w-full"
                                    />
                                )}
                                {onboardNewClientLink?.enabled && (
                                    <DropdownMenuItem
                                        asChild
                                        className="mt-1.5 truncate px-2 py-1.5 text-sm"
                                    >
                                        <Link
                                            href={onboardNewClientLink.href}
                                            className="flex w-full items-center gap-2 truncate"
                                        >
                                            <Plus className="size-4" />
                                            <span>
                                                {onboardNewClientLink.label}
                                            </span>
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                            </div>
                        )}

                        {/* Menu Items */}
                        {(standardItems.length > 0 ||
                            internalItems.length > 0) && (
                            <>
                                <Separator className="my-1" />
                                <DropdownMenuGroup>
                                    {standardItems.map(renderMenuLink)}
                                    {internalItems.length > 0 ? (
                                        <DropdownMenuSub>
                                            <DropdownMenuSubTrigger className="truncate px-2 py-1.5 text-sm">
                                                <Icon
                                                    type="wrench"
                                                    color="currentColor"
                                                    width={16}
                                                    height={16}
                                                />
                                                <span>Internal</span>
                                            </DropdownMenuSubTrigger>
                                            <DropdownMenuPortal>
                                                <DropdownMenuSubContent className="w-56">
                                                    {internalItems.map(
                                                        renderMenuLink,
                                                    )}
                                                </DropdownMenuSubContent>
                                            </DropdownMenuPortal>
                                        </DropdownMenuSub>
                                    ) : null}
                                </DropdownMenuGroup>
                            </>
                        )}

                        <Separator className="my-1" />

                        {/* Dark mode toggle */}
                        <DropdownMenuItem
                            className="truncate px-2 py-1.5 text-sm"
                            onSelect={(e) => {
                                e.preventDefault()
                                setTheme(isDark ? 'light' : 'dark')
                            }}
                        >
                            {isDark ? (
                                <Sun className="mr-2 size-4" />
                            ) : (
                                <Moon className="mr-2 size-4" />
                            )}
                            <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
                        </DropdownMenuItem>

                        {/* Logout */}
                        <DropdownMenuItem
                            className="truncate px-2 py-1.5 text-sm text-destructive focus:text-destructive"
                            onClick={handleLogout}
                        >
                            <LogOut className="mr-2 size-4" />
                            <span>Logout</span>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    )
}
